import {
  Input,
  Output,
  EventEmitter,
  Directive,
  Renderer2,
  ChangeDetectorRef,
  SimpleChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { PhxDomainConfig, PhxDomainOptions, PhxOmniOption, PhxDomains, ENTITY_STATUS } from './phx-omni-search-filter.types';
import { PhxTranslatePipe } from '../../translation/phx-translation.pipe';
import { PhxSelectOption, PhxSelectOptionContext } from '../select/select.types';
import { debounce } from '../../utils';

const ALL_DOMAIN_OPTION: PhxDomainConfig = {
  label: 'All',
  name: PhxDomainOptions.ALL,
  icon: '',
  placeholder: 'Entity names here',
  popularLabel: 'names',
}

// Common inputs and outputs for both components
@Directive()
export abstract class PhxOmniBaseComponent {
  // Common inputs
  @Input() phxDomains: PhxDomains;
  @Input() phxPlaceholder: string;
  @Input() phxDebounceTime: number = 300;
  @Input() phxDisabled: boolean = false;
  @Input() phxInputValue: string = '';
  @Input() phxShowByType: boolean = false;
  @Input() phxFlattenedOptions: PhxOmniOption[] = [];
  @Input() phxGroupedOptions: { [key in PhxDomainOptions]?: PhxOmniOption[] };
  @Input() phxLoading: boolean = false;

  // Common outputs
  @Output() phxSearch: EventEmitter<string> = new EventEmitter<string>();
  @Output() phxScrollEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() phxOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() phxClearAll: EventEmitter<void> = new EventEmitter<void>();
  @Output() phxShowByTypeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  // Common protected properties
  protected _cachedNoResultsMessage: string = '';
  protected _cachedFindByString: string = '';
  protected destroy$: Subject<void> = new Subject<void>();
  protected _activeFocusElement: number = -1;
  private _derivedComponentName: string;

  @ViewChild('deactivatedEntitySuffixTemplate') protected deactivatedEntityTemplate?: TemplateRef<{ $implicit: PhxSelectOptionContext }>;

  // Common public properties
  public phxOptions: PhxSelectOption[] = [];
  public shouldGroupOptions: boolean = false;
  public domainList: PhxDomainConfig[] = [];

  constructor(protected _renderer: Renderer2, protected _cdr: ChangeDetectorRef, protected _phxTranslate: PhxTranslatePipe) { }

  ngAfterViewInit(): void {
    if (this.deactivatedEntityTemplate) {
      this.formatOptions();
      this._cdr.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxFlattenedOptions, phxGroupedOptions, phxDomains } = changes;

    if (phxDomains && phxDomains.currentValue) {
      this.updateDomainList();
    }

    if (phxFlattenedOptions || phxGroupedOptions) {
      this.formatOptions();
    }
  }

  protected updateDomainList(): void {
    if (this.phxDomains) {
      this.domainList = Object.values(this.phxDomains);
      this._cdr.detectChanges();
    } else {
      this.domainList = [];
    }
  }

  public handleInput(event: Event): void {
    const value: string = (event.target as HTMLInputElement).value;
    this.phxInputValue = value;
    this.phxSearch.emit(value);
  }

  public debouncedHandleInput = debounce(this.handleInput.bind(this), this.phxDebounceTime);

  public onSearch(query: string): void {
    this.phxInputValue = query;
    this.phxSearch.emit(query);

    if (!query) {
      this.onSearchClear()
    }
  }

  public onSearchClear(): void {
    this.phxInputValue = '';
    this.phxSearch.emit('');
  }

  public onToggleChange(event: boolean): void {
    this.phxShowByTypeChange.emit(event);
    this.formatOptions();
  }

  public onClearClick(): void {
    this.phxClearAll.emit();
  }

  public onScrollEnd(): void {
    this.phxScrollEnd.emit();
  }

  public get noResultsMessage(): string {
    return this._cachedNoResultsMessage;
  }

  public get findByString(): string {
    return this._cachedFindByString;
  }

  public get isSearched(): boolean {
    return this.phxInputValue && this.phxInputValue.length >= 2;
  }

  protected formatOptions(): void {
    if (this.phxFlattenedOptions) {
      this.phxOptions = this.phxFlattenedOptions.map(this.transformToPhxSelectOption.bind(this));
    } else if (this.phxGroupedOptions) {
      const result: PhxSelectOption[] = [];
      for (const domain in this.phxGroupedOptions) {
        if (this.phxGroupedOptions.hasOwnProperty(domain)) {
          result.push({
            groupLabel: this.phxDomains[domain].label,
          });
          this.phxGroupedOptions[domain].forEach((option) => {
            result.push(this.transformToPhxSelectOption(option));
          });
        }
      }
      this.phxOptions = result;
    } else {
      this.phxOptions = [];
    }

    this.updateCachedMessages();
  }

  protected transformToPhxSelectOption(option: PhxOmniOption): PhxSelectOption {
    return {
      value: option.value,
      label: option.label,
      subText: option.subText,
      data: { ...(option.data || {}), domain: option.domain },
      prefixIcon: this.domainIcon(option.domain),
      shouldHighlightMatchedSubtext: option.shouldHighlightMatchedSubtext,
      suffixTemplate: option.status === ENTITY_STATUS.DEACTIVE ? this.deactivatedEntityTemplate : null,
    };
  }

  protected transformToOmniOption(option: PhxSelectOption): PhxOmniOption {
    return {
      value: option.value,
      label: option.label,
      subText: option.subText,
      domain: option.data?.domain,
      data: option.data
    };
  }

  protected setDerivedComponentName(componentName: string): void {
    this._derivedComponentName = componentName;
  }

  public domainIcon(domainName: string): string {
    return this.phxDomains?.[domainName]?.icon;
  }

  protected updateCachedMessages(domain: PhxDomainConfig = ALL_DOMAIN_OPTION): void {
    this._cachedFindByString = this.generateFindByString(domain);
    this._cachedNoResultsMessage = this.generateNoResultsMessage(domain);
  }

  private generateFindByString(domain: PhxDomainConfig): string {
    if (!this.phxDomains) {
      return this._phxTranslate.transform('Find by Keyword');
    }

    let stringPrefix: string = '';
    if (this._derivedComponentName === 'PhxOmnifilterComponent') {
      stringPrefix = this._phxTranslate.transform('Filter for');
    } else {
      stringPrefix = this._phxTranslate.transform('Find');
    }

    if (domain.name === PhxDomainOptions.ALL) {
      const relevantDomains: PhxDomainConfig[] = Object.values(this.phxDomains).filter((d) => d.name !== PhxDomainOptions.ALL);

      const popularLabels: string[] = relevantDomains.filter((d) => d.popularLabel).map((d) => d.popularLabel.toLowerCase());

      const uniqueSearchKeys: Set<string> = new Set<string>();
      relevantDomains.forEach((domainConfig) => {
        if (domainConfig.searchKeys) {
          domainConfig.searchKeys.forEach((key) => uniqueSearchKeys.add(key));
        }
      });

      const searchKeysArray: string[] = Array.from(uniqueSearchKeys);

      if (popularLabels.length > 0 && searchKeysArray.length > 0) {
        const popularLabelsText: string = this.formatListWithOr(popularLabels);
        const searchKeysText: string = this.formatListWithOr(searchKeysArray);

        return `${stringPrefix} ${popularLabelsText} ${this._phxTranslate.transform('by')} ${searchKeysText}.`;
      }
    } else if (domain.popularLabel && domain.searchKeys && domain.searchKeys.length > 0) {
      return `${stringPrefix} ${domain.popularLabel.toLowerCase()} ${this._phxTranslate.transform('by')} ${this.formatListWithOr(
        domain.searchKeys
      )}.`;
    }

    // Fallback to a generic message
    return this._phxTranslate.transform('Find by Keyword');
  }

  private generateNoResultsMessage(domain: PhxDomainConfig): string {
    if (!this.phxDomains) {
      return this._phxTranslate.transform('Try searching by keyword.');
    }

    if (domain.name === PhxDomainOptions.ALL) {
      const relevantDomains: PhxDomainConfig[] = Object.values(this.phxDomains).filter((d) => d.name !== PhxDomainOptions.ALL);

      const popularLabels: string[] = relevantDomains.filter((d) => d.popularLabel).map((d) => d.popularLabel.toLowerCase());

      const uniqueSearchKeys: Set<string> = new Set<string>();
      relevantDomains.forEach((domainConfig) => {
        if (domainConfig.searchKeys) {
          domainConfig.searchKeys.forEach((key) => uniqueSearchKeys.add(key));
        }
      });

      const searchKeysArray: string[] = Array.from(uniqueSearchKeys);

      if (popularLabels.length > 0 && searchKeysArray.length > 0) {
        const popularLabelsText: string = this.formatListWithOr(popularLabels);
        const searchKeysText: string = this.formatListWithOr(searchKeysArray);

        return `${this._phxTranslate.transform('Try searching for')} ${popularLabelsText} ${this._phxTranslate.transform('by')} ${searchKeysText}.`;
      }
    } else {
      if (domain.popularLabel && domain.searchKeys && domain.searchKeys.length > 0) {
        return `${this._phxTranslate.transform('Try searching for')} ${domain.popularLabel.toLowerCase()} ${this._phxTranslate.transform(
          'by'
        )} ${this.formatListWithOr(domain.searchKeys)}.`;
      }
    }

    return this._phxTranslate.transform(`Try searching by keyword.`);
  }

  private formatListWithOr(items: string[]): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];

    const lastItem: string = items[items.length - 1];
    const otherItems: string[] = items.slice(0, items.length - 1);

    return `${otherItems.join(', ')}, or ${lastItem}`;
  }
}
